import { lessonsFor, mc, tf } from "./helpers";

const checklist = { label: "Security Checklist", href: "/security-checklist" };
const guide = { label: "AI Safety Guide", href: "/security-guide" };

export const CYBERSECURITY_LESSONS = lessonsFor("cybersecurity", [
  {
    slug: "spotting-phishing",
    title: "Spotting Phishing Emails",
    minutes: 6,
    learn: ["Common warning signs in a phishing email", "What to do instead of clicking"],
    explanation: [
      "Phishing emails pretend to be someone you trust to get you to click, pay or share a password.",
      "Watch for urgency, unexpected attachments, links that don't match the sender, and requests for credentials or payment.",
    ],
    example: "An email \"from IT\" says your mailbox will be deleted in 1 hour unless you log in through a link.",
    interactive: "spot-phish-email",
    check: mc("multi", "Which are common phishing signs? (Choose all that apply)", ["*Extreme urgency", "*A link that doesn't match the company's real site", "An email you expected from a colleague, with no requests", "*A request to confirm your password"], "Urgency, mismatched links and credential requests are classic signs."),
    takeaway: "Slow down. Go to the real site yourself instead of clicking.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "smishing",
    title: "Smishing — Suspicious Text Messages",
    minutes: 4,
    learn: ["How text-message scams work", "Safe ways to check a text"],
    explanation: ["Smishing is phishing by text message: fake delivery notices, bank alerts or toll payments with a link."],
    example: "\"Your package is on hold. Pay a $1.99 fee: [short link]\" — from a number you don't know.",
    interactive: "spot-phish-sms",
    check: mc("scenario", "You get a text saying your bank card is locked, with a link. What's safest?", ["Tap the link quickly", "*Open your bank's app or call the number on your card", "Reply STOP"], "Contact the bank through a channel you already trust."),
    takeaway: "Don't tap links in unexpected texts — check through the official app or number.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "vishing",
    title: "Vishing — Suspicious Phone Calls",
    minutes: 4,
    learn: ["Pressure tactics used on the phone", "How to end and verify a call"],
    explanation: ["Vishing is voice phishing. Callers pose as banks, tech support or government agencies and push you to act right away."],
    example: "A caller says they're from your bank's fraud team and asks you to read back a one-time code.",
    interactive: "spot-phish-voice",
    check: tf("A real bank may ask you to read out a one-time security code sent to your phone.", false, "Those codes are for you only. Hang up and call the number on your card."),
    takeaway: "Hang up and call back on a number you look up yourself.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "passphrases",
    title: "Password and Passphrase Hygiene",
    minutes: 4,
    learn: ["Why length beats complexity", "Using a password manager"],
    explanation: [
      "A long passphrase of random words is easier to remember and harder to guess than a short complex password.",
      "Use a different password for every account; a password manager makes this practical.",
    ],
    example: "\"lantern-ocean-pickle-violin\" is stronger and easier to remember than \"P@ss1!\".",
    check: mc("single", "What's the biggest password risk?", ["Using a passphrase", "*Reusing the same password on many sites", "Using a password manager"], "If one site leaks, reused passwords unlock your other accounts."),
    takeaway: "Long, unique, and stored in a password manager.",
    tool: { label: "All tools (password generator)", href: "/tools" },
    resource: checklist,
  },
  {
    slug: "mfa",
    title: "Multi-Factor Authentication",
    minutes: 4,
    learn: ["What MFA adds", "Choosing stronger second factors"],
    explanation: [
      "Multi-factor authentication asks for something extra beyond your password, like an app code or a security key.",
      "Authenticator apps and security keys are generally stronger than text-message codes, but any MFA is better than none.",
    ],
    example: "Even if someone steals your password, they can't sign in without the code from your phone's authenticator app.",
    check: tf("Turning on MFA helps even if your password leaks.", true, "The attacker would still need your second factor."),
    takeaway: "Turn on MFA for email, banking and work accounts first.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "social-engineering",
    title: "Social Engineering",
    minutes: 5,
    learn: ["How scammers exploit trust and emotion", "Spotting manipulation"],
    explanation: ["Social engineering manipulates people rather than computers — using fear, urgency, authority or kindness to get you to break normal rules."],
    example: "Someone in a delivery uniform asks you to hold the secure office door because their hands are full.",
    check: mc("single", "Which emotion do scammers most often use to rush you?", ["Boredom", "*Urgency or fear", "Calm"], "Pressure stops people from thinking it through."),
    takeaway: "If you feel rushed, pause — that's the signal.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "links-attachments",
    title: "Suspicious Links and Attachments",
    minutes: 4,
    learn: ["Checking a link before you trust it", "Handling unexpected files"],
    explanation: [
      "On a computer, hover over a link to see where it really goes. On a phone, press and hold to preview.",
      "Be careful with unexpected attachments, especially ones asking you to enable macros or editing.",
    ],
    example: "Link text says \"yourbank.com\" but the preview shows \"yourbank-secure-login.example\".",
    check: tf("The text of a link always shows where it really goes.", false, "Link text can say anything; the actual destination can differ."),
    takeaway: "Preview links and question unexpected files.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "ai-scams",
    title: "AI-Powered Scams and Impersonation",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Voice clones and deepfake messages", "Family and workplace code-word habits"],
    explanation: [
      "AI makes it easier to fake voices, faces and convincing writing. A familiar voice asking for urgent money may not be real.",
      "Agree on a verification habit — a code word or calling back on a known number — for urgent money or data requests.",
    ],
    example: "A call that sounds like your manager asks you to buy gift cards today. You hang up and call their known number.",
    check: mc("scenario", "A voice that sounds like a relative asks for money urgently. What's best?", ["Send it right away", "*Hang up and call them back on their known number", "Ask them to text instead"], "Calling back on a known number defeats voice cloning."),
    takeaway: "Familiar isn't proof. Verify urgent requests another way.",
    tool: checklist,
    resource: { label: "Security articles", href: "/blogs" },
  },
  {
    slug: "protect-accounts-devices",
    title: "Protecting Accounts and Devices",
    minutes: 4,
    learn: ["Updates, screen locks and backups", "Reviewing account activity"],
    explanation: ["Keep devices updated, use a screen lock, back up important files, and occasionally review sign-in activity on key accounts."],
    example: "Turning on automatic updates fixes known security holes without you having to remember.",
    check: tf("Software updates often include security fixes.", true, "Updates close known weaknesses attackers use."),
    takeaway: "Update, lock, back up.",
    tool: checklist,
    resource: guide,
  },
  {
    slug: "when-something-feels-wrong",
    title: "What to Do When Something Feels Wrong",
    minutes: 4,
    learn: ["First steps after a suspicious click", "Who to tell"],
    explanation: [
      "If you clicked something suspicious or shared a password, act fast: change the password, turn on MFA, and tell your IT team or bank.",
      "Reporting quickly is not a failure — it helps limit the damage.",
    ],
    example: "You entered your password on a fake page. You change it right away from the real site and report it to IT.",
    check: mc("single", "You think you entered your password on a fake site. First step?", ["Wait and see", "*Change the password from the real site and report it", "Delete the email and forget it"], "Fast action and reporting limit harm."),
    takeaway: "Change it, report it, and don't be embarrassed.",
    tool: checklist,
    resource: guide,
  },
]);
